import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { buildRotatingScanLink, buildScanLink } from '../utils/rotatingQr';

/**
 * Ticket QR image that rotates on the admin-configured window while QR
 * rotation is enabled (ticket carries `rotation_seed`). Otherwise renders the
 * static scan link (or the backend-provided image when no code is known).
 *
 * QR images are generated LOCALLY via the `qrcode` package — no external QR
 * image API is called on the rotating hot path, so thousands of concurrent
 * dashboards can't be rate-limited by a third party (and a throttle could
 * never freeze an attendee on a stale QR).
 */
const RotatingQrImg = ({ ticket, size = 240, alt = 'Ticket QR', style, className }) => {
  const seed = ticket?.rotation_seed;
  const ticketCode = ticket?.ticket_code;
  const fallback = ticket?.qr_code || '';
  const [src, setSrc] = useState(fallback);

  useEffect(() => {
    if (!ticketCode) {
      setSrc(fallback);
      return undefined;
    }
    let cancelled = false;
    const update = async () => {
      try {
        const link = seed ? await buildRotatingScanLink(ticket) : buildScanLink(ticketCode);
        const dataUrl = await QRCode.toDataURL(link, {
          width: size,
          margin: 0,
          // H (30% reconstruction budget) — required so the themed center
          // emblem in AnimatedQrFrame can never cost a gate scan.
          errorCorrectionLevel: 'H',
        });
        if (!cancelled) setSrc(dataUrl);
      } catch {
        if (!cancelled) setSrc(fallback);
      }
    };
    update();
    // Cheap local recompute — the token only actually changes when the
    // window flips, and no network request is involved either way.
    const timer = setInterval(update, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [seed, ticketCode, fallback, size, ticket]);

  if (!src) return null;
  return <img loading="lazy" src={src} alt={alt} style={style} className={className} />;
};

export default RotatingQrImg;
